import { InputFiles } from '@/components/futuristic-editor/file-utils';

export interface DetectedRoute {
    path: string;
    type: 'page' | 'app';
    filePath: string;
}

export interface RouteDetectionResult {
    routes: DetectedRoute[];
    hasAppRouter: boolean;
    hasPagesRouter: boolean;
    appDirectory?: string;
    pagesDirectory?: string;
}

/**
 * Detects only static, navigable routes in a Next.js application,
 * focusing on routes that would appear in a search bar for direct navigation.
 * Ignores dynamic routes, API routes, and public static files.
 *
 * This version is improved to handle deep nested routes for both App and Pages Routers,
 * and correctly resolves 'index.tsx' in subdirectories for Pages Router,
 * and 'page.tsx' in subdirectories for App Router.
 */
export const detectRoutes = (files: InputFiles): RouteDetectionResult => {
    const routes: DetectedRoute[] = [];
    let hasAppRouter = false;
    let hasPagesRouter = false;
    let appDirectory = '';
    let pagesDirectory = '';

    const filePaths = Object.keys(files);

    // Determine base directories (e.g., "app/", "src/app/")
    const appDirMatch = filePaths.find(path => path.startsWith('app/') || path.startsWith('src/app/'));
    if (appDirMatch) {
        hasAppRouter = true;
        appDirectory = appDirMatch.startsWith('src/') ? 'src/app/' : 'app/';
    }

    const pagesDirMatch = filePaths.find(path => path.startsWith('pages/') || path.startsWith('src/pages/'));
    if (pagesDirMatch) {
        hasPagesRouter = true;
        pagesDirectory = pagesDirMatch.startsWith('src/') ? 'src/pages/' : 'pages/';
    }

    if (hasAppRouter) {
        const appRoutes = detectAppRouterNavigableRoutes(filePaths, appDirectory);
        routes.push(...appRoutes);
    }

    if (hasPagesRouter) {
        const pagesRoutes = detectPagesRouterNavigableRoutes(filePaths, pagesDirectory);
        routes.push(...pagesRoutes);
    }

    // Filter out duplicate routes and sort
    const uniqueRoutes = routes.filter((route, index, self) =>
        index === self.findIndex(r => r.path === route.path)
    );

    return {
        routes: uniqueRoutes.sort((a, b) => a.path.localeCompare(b.path)),
        hasAppRouter,
        hasPagesRouter,
        appDirectory,
        pagesDirectory
    };
};

/**
 * Detects static, navigable routes in App Router (app/ directory).
 * Skips any dynamic segments.
 */
const detectAppRouterNavigableRoutes = (filePaths: string[], appDirectory: string): DetectedRoute[] => {
    const routes: DetectedRoute[] = [];
    const appDirPrefixRegex = new RegExp(`^${escapeRegExp(appDirectory)}`);

    for (const filePath of filePaths) {
        if (!filePath.startsWith(appDirectory)) continue;

        // relativePath will be like 'page.tsx', 'about/page.tsx', 'blog/[slug]/page.tsx'
        const relativePath = filePath.replace(appDirPrefixRegex, '');

        // 1. Check if it's a valid page file (e.g., 'page.tsx')
        if (!isPageFile(relativePath)) {
            continue;
        }

        // 2. Check if the path contains any dynamic segments BEFORE conversion
        // This ensures 'blog/[slug]/page.tsx' is skipped.
        if (containsDynamicSegment(relativePath)) {
            continue;
        }

        const route = convertAppPathToNavigableRoute(relativePath);
        if (route) {
            routes.push({
                path: route.path,
                type: 'app',
                filePath,
            });
        }
    }
    return routes;
};

/**
 * Detects static, navigable routes in Pages Router (pages/ directory).
 * Skips any dynamic segments.
 */
const detectPagesRouterNavigableRoutes = (filePaths: string[], pagesDirectory: string): DetectedRoute[] => {
    const routes: DetectedRoute[] = [];
    const pagesDirPrefixRegex = new RegExp(`^${escapeRegExp(pagesDirectory)}`);

    for (const filePath of filePaths) {
        if (!filePath.startsWith(pagesDirectory)) continue;

        // relativePath will be like 'index.tsx', 'about.tsx', 'blog/[slug].tsx', 'folder/index.tsx'
        const relativePath = filePath.replace(pagesDirPrefixRegex, '');
        const fileName = relativePath.split('/').pop() || '';

        // 1. Skip non-route files (starting with _), and non-route extensions.
        if (fileName.startsWith('_') || !isRouteFile(relativePath)) {
            continue;
        }

        // 2. Check if the path contains any dynamic segments BEFORE conversion.
        // This ensures 'blog/[slug].tsx' or 'folder/[id].tsx' are skipped.
        if (containsDynamicSegment(relativePath)) {
            continue;
        }

        const route = convertPagesPathToNavigableRoute(relativePath);
        if (route) {
            routes.push({
                path: route.path,
                type: 'page',
                filePath,
            });
        }
    }
    return routes;
};

/**
 * Converts an App Router relative path (e.g., 'about/sub/page.tsx') to a navigable route (e.g., '/about/sub').
 * Returns null if the path is invalid or if a dynamic segment somehow slipped through.
 */
const convertAppPathToNavigableRoute = (relativePath: string): { path: string } | null => {
    // Should already be handled by detectAppRouterNavigableRoutes, but as a safeguard:
    if (containsDynamicSegment(relativePath)) {
        return null;
    }

    // Remove the 'page.ext' from the end of the path
    // 'about/sub/page.tsx' -> 'about/sub'
    // 'page.tsx' -> ''
    const pathWithoutPageFile = relativePath.replace(/(?:\/|^)page\.(tsx?|jsx?|ts|js)$/, '');

    // Handle root path
    if (pathWithoutPageFile === '') {
        return { path: '/' };
    }

    // Ensure leading slash for non-root paths
    return { path: '/' + pathWithoutPageFile };
};

/**
 * Converts a Pages Router relative path (e.g., 'about/sub.tsx', 'folder/index.tsx') to a navigable route.
 * Returns null if the path is invalid or if a dynamic segment somehow slipped through.
 */
const convertPagesPathToNavigableRoute = (relativePath: string): { path: string } | null => {
    // Should already be handled by detectPagesRouterNavigableRoutes, but as a safeguard:
    if (containsDynamicSegment(relativePath)) {
        return null;
    }

    // Remove file extension
    // 'about/sub.tsx' -> 'about/sub'
    // 'index.tsx' -> 'index'
    // 'folder/index.tsx' -> 'folder/index'
    const pathWithoutExt = relativePath.replace(/\.(tsx?|jsx?|ts|js)$/, '');

    // Handle root index (pages/index.tsx)
    if (pathWithoutExt === 'index') {
        return { path: '/' };
    }

    // Handle index files in subdirectories (e.g., pages/folder/index.tsx -> /folder)
    // 'folder/index' -> 'folder'
    const finalPath = pathWithoutExt.endsWith('/index')
        ? pathWithoutExt.substring(0, pathWithoutExt.length - '/index'.length)
        : pathWithoutExt;

    // Ensure leading slash for non-root paths
    return { path: '/' + finalPath };
};

/**
 * Checks if a file is a route file (for Pages Router and API routes).
 */
const isRouteFile = (path: string): boolean => {
    const routeExtensions = ['.tsx', '.jsx', '.ts', '.js'];
    return routeExtensions.some(ext => path.endsWith(ext));
};

/**
 * Checks if a file is a page file (for App Router).
 */
const isPageFile = (path: string): boolean => {
    const pageFileNames = ['page.tsx', 'page.jsx', 'page.ts', 'page.js'];
    // Checks if the path *ends* with any of the valid page filenames
    // This handles 'about/sub/page.tsx' -> true
    return pageFileNames.some(fileName => path.endsWith(fileName));
};

/**
 * Checks if a path segment contains dynamic patterns like `[param]`, `[[...param]]`, `[...param]`.
 * This regex is designed to catch any segment that looks like a Next.js dynamic route.
 */
const containsDynamicSegment = (path: string): boolean => {
    // Regex to match any part of the path that is enclosed in square brackets.
    // e.g., '[slug]', '[...slug]', '[[...slug]]'
    return /(\/|^)\[[^\]]+\]/.test(path);
};

/**
 * Helper to escape special characters in a string for use in a RegExp constructor.
 */
const escapeRegExp = (string: string): string => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
};

/**
 * Generates route suggestions for URL search component.
 */
export const generateRouteSuggestions = (routes: DetectedRoute[]): Array<{ value: string; label: string; type: string }> => {
    return routes.map(route => ({
        value: route.path,
        label: route.path,
        type: route.type
    }));
};